(function () {
  "use strict";

  // 1 US gallon = 3.785411784 L (tam tanim). 1 m3 = 264.172052 gal.
  var L_PER_GAL = 3.785411784;
  var GAL_PER_M3 = 264.172052;

  function getUnitSystem(root) {
    if (!root) return "metric";
    var u = root.getAttribute("data-unit-system");
    return u === "imperial" ? "imperial" : "metric";
  }

  var defaults = {
    maleEmployees: 100,
    employeeUsagePerDay: 2,
    maleVisitors: 5000,
    visitorUrinalUsagePercent: 10,
    urinalCount: 10,
    waterPerUse: 3,
    waterUnitPrice: 185,
    operatingDays: 300,
    cleaningCost: 0,
    maintenanceCost: 0,
    cartridgeCapacity: 17500,
    cartridgePrice: 760,
    unitPrice: 14000,
  };

  function getValue(root, id) {
    var el = root.querySelector("#" + id);
    if (!el) return 0;
    var value = parseFloat(el.value, 10);
    return Number.isFinite(value) ? value : 0;
  }

  function syncSegmentButtons(root, id, rawValue) {
    var wrap = root.querySelector('[data-segment-input="' + id + '"]');
    if (!wrap) return;
    var num = parseFloat(rawValue, 10);
    wrap.querySelectorAll(".urinal-savings-seg").forEach(function (btn) {
      var bv = parseFloat(btn.getAttribute("data-value"), 10);
      var match =
        Number.isFinite(num) &&
        Number.isFinite(bv) &&
        Math.abs(num - bv) < 1e-6;
      btn.classList.toggle("is-active", match);
      btn.setAttribute("aria-pressed", match ? "true" : "false");
    });
  }

  function setValue(root, id, value) {
    var input = root.querySelector("#" + id);
    var range = root.querySelector('.calc-range[data-input="' + id + '"]');
    if (input) input.value = value;
    if (range) {
      range.value = value;
      updateRangeBackground(range);
    }
    syncSegmentButtons(root, id, value);
  }

  function initSegments(root) {
    root.querySelectorAll("[data-segment-input]").forEach(function (wrap) {
      var inputId = wrap.getAttribute("data-segment-input");
      var target = root.querySelector("#" + inputId);
      if (!target) return;
      wrap.querySelectorAll(".urinal-savings-seg").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var val = btn.getAttribute("data-value");
          target.value = val;
          syncSegmentButtons(root, inputId, val);
          calculate(root);
        });
      });
      syncSegmentButtons(root, inputId, target.value);
    });
  }

  function clampInput(input, value) {
    var min = parseFloat(input.getAttribute("min"), 10);
    var max = parseFloat(input.getAttribute("max"), 10);
    var nextValue = Number.isFinite(value) ? value : 0;
    if (Number.isFinite(min)) nextValue = Math.max(min, nextValue);
    if (Number.isFinite(max)) nextValue = Math.min(max, nextValue);
    return nextValue;
  }

  function getNumberLocale(root) {
    if (!root) return "de-DE";
    var loc = root.getAttribute("data-number-locale");
    return loc && loc.trim() ? loc.trim() : "de-DE";
  }

  function getShopCurrency(root) {
    if (!root) return "EUR";
    var c = root.getAttribute("data-shop-currency");
    return c && c.trim() ? c.trim() : "EUR";
  }

  function getPaybackLabel(root, kind) {
    if (!root) {
      if (kind === "singular") return "Monat";
      if (kind === "plural") return "Monate";
      return "Nicht berechenbar";
    }
    var attr =
      kind === "singular"
        ? "data-payback-month-singular"
        : kind === "plural"
        ? "data-payback-month-plural"
        : "data-payback-not-calculable";
    var value = root.getAttribute(attr);
    if (value && value.trim()) return value.trim();
    if (kind === "singular") return "Monat";
    if (kind === "plural") return "Monate";
    return "Nicht berechenbar";
  }

  function formatNumber(root, value, digits) {
    if (digits === undefined) digits = 0;
    return new Intl.NumberFormat(getNumberLocale(root), {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  }

  function formatCurrency(root, value) {
    var locale = getNumberLocale(root);
    var currency = getShopCurrency(root);
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (e) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }
  }

  function updateRangeBackground(range) {
    var min = parseFloat(range.getAttribute("min"), 10) || 0;
    var max = parseFloat(range.getAttribute("max"), 10) || 100;
    var value = parseFloat(range.value, 10) || 0;
    var progress = max === min ? 0 : ((value - min) / (max - min)) * 100;
    range.style.setProperty("--range-progress", progress + "%");
  }

  function setText(root, id, text) {
    var el = root.querySelector("#" + id);
    if (el) el.textContent = text;
  }

  function calculate(root) {
    var maleEmployees = getValue(root, "maleEmployees");
    var employeeUsagePerDay = getValue(root, "employeeUsagePerDay");
    var maleVisitors = getValue(root, "maleVisitors");
    var visitorUrinalUsagePercent = getValue(root, "visitorUrinalUsagePercent");
    var urinalCount = getValue(root, "urinalCount");
    var waterPerUse = getValue(root, "waterPerUse");
    var waterUnitPrice = getValue(root, "waterUnitPrice");
    var operatingDays = getValue(root, "operatingDays");
    var cleaningCost = getValue(root, "cleaningCost");
    var maintenanceCost = getValue(root, "maintenanceCost");
    var cartridgeCapacity = getValue(root, "cartridgeCapacity");
    var cartridgePrice = getValue(root, "cartridgePrice");
    var unitPrice = getValue(root, "unitPrice");

    var employeeDailyUsage = maleEmployees * employeeUsagePerDay;
    var visitorDailyUsage =
      maleVisitors * (visitorUrinalUsagePercent / 100);
    var totalDailyUsage = employeeDailyUsage + visitorDailyUsage;
    var annualUsage = totalDailyUsage * operatingDays;
    var imperial = getUnitSystem(root) === "imperial";

    // Imperial-Eingaben für die interne Berechnung in metrische Werte umrechnen; die Formel bleibt gleich.
    var waterPerUseL = imperial ? waterPerUse * L_PER_GAL : waterPerUse;
    var waterPricePerM3 = imperial
      ? (waterUnitPrice / 1000) * GAL_PER_M3
      : waterUnitPrice;

    var annualWaterLiters = annualUsage * waterPerUseL;
    var annualWaterM3 = annualWaterLiters / 1000;
    var annualWaterCost = annualWaterM3 * waterPricePerM3;
    var annualMaintenanceTotal = cleaningCost + maintenanceCost;
    var rawCartridgeNeed =
      cartridgeCapacity > 0 ? annualUsage / cartridgeCapacity : 0;
    var cartridgeCount = Math.ceil(rawCartridgeNeed);
    var waterlessAnnualTotal = rawCartridgeNeed * cartridgePrice;
    var conventionalAnnualTotal = annualWaterCost + annualMaintenanceTotal;
    var annualSavings = conventionalAnnualTotal - waterlessAnnualTotal;
    var investmentTotal = urinalCount * unitPrice;
    var paybackMonthsValue =
      annualSavings > 0 ? (investmentTotal / annualSavings) * 12 : null;
    var paybackMonths =
      paybackMonthsValue !== null
        ? (function (n) {
            return (
              n +
              " " +
              (n === 1
                ? getPaybackLabel(root, "singular")
                : getPaybackLabel(root, "plural"))
            );
          })(Math.round(paybackMonthsValue))
        : getPaybackLabel(root, "na");

    setText(root, "dailyUsage", formatNumber(root, totalDailyUsage));
    setText(root, "annualUsage", formatNumber(root, annualUsage));
    setText(root, "conventionalTotal", formatCurrency(root, conventionalAnnualTotal));
    setText(root, "waterlessTotal", formatCurrency(root, waterlessAnnualTotal));
    setText(root, "annualSavings", formatCurrency(root, annualSavings));
    setText(root, "paybackMonths", paybackMonths);

    setText(root, "tableEmployeeUsage", formatNumber(root, employeeDailyUsage));
    setText(root, "tableVisitorUsage", formatNumber(root, visitorDailyUsage));
    setText(root, "tableUrinalCount", formatNumber(root, urinalCount));
    setText(
      root,
      "tableWaterPerUse",
      imperial
        ? formatNumber(root, waterPerUse, 3) + " gal"
        : formatNumber(root, waterPerUse, 1) + " l"
    );
    setText(root, "tableWaterUnitPrice", formatCurrency(root, waterUnitPrice));
    setText(root, "tableOperatingDays", formatNumber(root, operatingDays));
    setText(
      root,
      "tableWaterLiters",
      imperial
        ? formatNumber(root, annualWaterLiters / L_PER_GAL) + " gal"
        : formatNumber(root, annualWaterLiters) + " l"
    );
    setText(
      root,
      "tableWaterM3",
      imperial
        ? formatNumber(root, (annualWaterM3 * GAL_PER_M3) / 1000, 1) + " kgal"
        : formatNumber(root, annualWaterM3, 2) + " m³"
    );
    setText(root, "tableWaterCost", formatCurrency(root, annualWaterCost));
    setText(root, "tableMaintenanceTotal", formatCurrency(root, annualMaintenanceTotal));
    setText(root, "tableAnnualUsage", formatNumber(root, annualUsage));
    setText(root, "tableCartridgeCount", formatNumber(root, cartridgeCount));
    setText(root, "tableWaterlessTotal", formatCurrency(root, waterlessAnnualTotal));
    setText(root, "tableInvestmentTotal", formatCurrency(root, investmentTotal));
    setText(root, "tableSavings", formatCurrency(root, annualSavings));
    setText(root, "tablePayback", paybackMonths);
  }

  function initHelp(root) {
    function closeAllHelp() {
      root.querySelectorAll(".urinal-savings-help-panel").forEach(function (p) {
        p.classList.remove("is-open");
        p.setAttribute("aria-hidden", "true");
      });
      root.querySelectorAll(".urinal-savings-help-btn").forEach(function (b) {
        b.setAttribute("aria-expanded", "false");
      });
    }

    root.querySelectorAll(".urinal-savings-help-btn").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        var panelId = btn.getAttribute("aria-controls");
        var panelEl = panelId ? document.getElementById(panelId) : null;
        if (!panelEl) return;
        var wasOpen = panelEl.classList.contains("is-open");
        closeAllHelp();
        if (!wasOpen) {
          panelEl.classList.add("is-open");
          panelEl.setAttribute("aria-hidden", "false");
          btn.setAttribute("aria-expanded", "true");
        }
      });
    });

    document.addEventListener("click", function (e) {
      if (!root.contains(e.target)) {
        closeAllHelp();
        return;
      }
      if (
        e.target.closest(".urinal-savings-help-btn") ||
        e.target.closest(".urinal-savings-help-panel")
      ) {
        return;
      }
      closeAllHelp();
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeAllHelp();
    });
  }

  function init(root) {
    if (!root) return;

    var ranges = root.querySelectorAll(".calc-range");
    ranges.forEach(function (range) {
      updateRangeBackground(range);
      var inputId = range.getAttribute("data-input");
      if (!inputId) return;
      function syncFromRange() {
        var numInput = root.querySelector("#" + inputId);
        if (numInput) numInput.value = range.value;
        updateRangeBackground(range);
        calculate(root);
      }
      range.addEventListener("input", syncFromRange);
      range.addEventListener("change", syncFromRange);
    });

    root.querySelectorAll("input[type='number']").forEach(function (input) {
      var id = input.id;
      if (!id) return;
      function syncFromInput() {
        var nextValue = clampInput(input, parseFloat(input.value, 10));
        input.value = nextValue;
        var range = root.querySelector('.calc-range[data-input="' + id + '"]');
        if (range) {
          range.value = nextValue;
          updateRangeBackground(range);
        }
        calculate(root);
      }
      input.addEventListener("input", syncFromInput);
      input.addEventListener("change", syncFromInput);
    });

    initSegments(root);

    var resetBtn = root.querySelector("#resetDefaults");
    if (resetBtn) {
      resetBtn.addEventListener("click", function () {
        Object.keys(defaults).forEach(function (key) {
          setValue(root, key, defaults[key]);
        });
        ranges.forEach(updateRangeBackground);
        calculate(root);
      });
    }

    initHelp(root);
    calculate(root);
  }

  if (typeof window !== "undefined") {
    window.PremiumCalculator = { init: init, defaults: defaults };
  }
})();
